type CountryCode = 'MX' | 'CO' | 'PE' | 'CL' | 'ES' | 'US'
type CurrencyCode = 'MXN' | 'COP' | 'PEN' | 'CLP' | 'EUR' | 'USD'

interface CountryConfig {
  currency: CurrencyCode
  // [product1 (28d), product2 (12w), product3 (24w)]
  prices: [number, number, number]
  // Multiplier for "original" strikethrough price
  originalMultiplier: number
}

const COUNTRY_MAP: Record<CountryCode, CountryConfig> = {
  MX: { currency: 'MXN', prices: [299, 499, 699], originalMultiplier: 2.3 },
  CO: {
    currency: 'COP',
    prices: [69000, 109000, 149000],
    originalMultiplier: 2.3,
  },
  PE: { currency: 'PEN', prices: [65, 99, 139], originalMultiplier: 2.3 },
  CL: {
    currency: 'CLP',
    prices: [15900, 25900, 35900],
    originalMultiplier: 2.3,
  },
  ES: { currency: 'EUR', prices: [17, 27, 39], originalMultiplier: 2.3 },
  US: { currency: 'USD', prices: [17, 27, 39], originalMultiplier: 2.3 },
}

const PLAN_DAYS = [28, 84, 168] // 28 days, 12 weeks, 24 weeks

function detectCountry(): CountryCode {
  try {
    // Use navigator.languages for more precise detection
    const langs = navigator.languages
      ? navigator.languages.join().toLowerCase()
      : (navigator.language || '').toLowerCase()

    if (langs.includes('es-mx')) return 'MX'
    if (langs.includes('es-co')) return 'CO'
    if (langs.includes('es-pe')) return 'PE'
    if (langs.includes('es-cl')) return 'CL'
    if (langs.includes('es-es')) return 'ES'

    // Fallback: check timezone
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    if (
      tz.includes('Mexico') ||
      tz.includes('America/Cancun') ||
      tz.includes('America/Merida') ||
      tz.includes('America/Monterrey') ||
      tz.includes('America/Chihuahua') ||
      tz.includes('America/Hermosillo') ||
      tz.includes('America/Mazatlan') ||
      tz.includes('America/Tijuana')
    )
      return 'MX'
    if (tz.includes('Bogota')) return 'CO'
    if (tz.includes('Lima')) return 'PE'
    if (tz.includes('Santiago')) return 'CL'
    if (tz.includes('Madrid')) return 'ES'
  } catch {
    // Silent fail
  }

  return 'US'
}

function formatNumber(num: number, useDot: boolean): string {
  if (useDot) {
    // COP/CLP style: 69.000
    return num.toLocaleString('de-DE', { maximumFractionDigits: 0 })
  }
  return num.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

export function formatPrice(amount: number, currency: CurrencyCode): string {
  switch (currency) {
    case 'MXN':
      return `$${formatNumber(Math.round(amount), false)} MXN`
    case 'COP':
      return `$${formatNumber(Math.round(amount), true)} COP`
    case 'PEN':
      return `S/${formatNumber(Math.round(amount), false)}`
    case 'CLP':
      return `$${formatNumber(Math.round(amount), true)} CLP`
    case 'EUR':
      return `${formatNumber(Math.round(amount), false)}€`
    case 'USD':
    default:
      return `$${amount % 1 === 0 ? Math.round(amount) : amount.toFixed(2)}`
  }
}

export function formatPerDay(amount: number, currency: CurrencyCode): string {
  switch (currency) {
    case 'MXN':
      return `$${amount.toFixed(amount >= 10 ? 0 : 2)} MXN`
    case 'COP':
      return `$${formatNumber(Math.round(amount), true)} COP`
    case 'PEN':
      return `S/${amount.toFixed(amount >= 10 ? 0 : 2)}`
    case 'CLP':
      return `$${formatNumber(Math.round(amount), true)} CLP`
    case 'EUR':
      return `${amount.toFixed(2)}€`
    case 'USD':
    default:
      return `$${amount.toFixed(2)}`
  }
}

export interface LocalizedPlan {
  id: string
  label: string
  price: number
  originalPrice: number
  perDay: number
  formattedPrice: string
  formattedOriginalPrice: string
  formattedPerDay: string
  currency: CurrencyCode
  popular: boolean
  url: string
}

const PLAN_META = [
  {
    id: '28days',
    label: 'RUTINA DE 28 DÍAS',
    popular: true,
    url: 'https://recetariosaludable.lat/cart/42433966506069:1',
  },
  {
    id: '12weeks',
    label: 'RUTINA DE 12 SEMANAS',
    popular: false,
    url: 'https://recetariosaludable.lat/cart/42434575171669:1',
  },
  {
    id: '24weeks',
    label: 'RUTINA DE 24 SEMANAS',
    popular: false,
    url: 'https://recetariosaludable.lat/cart/42434577072213:1',
  },
]

export function getLocalizedPlans(): LocalizedPlan[] {
  const country = detectCountry()
  const config = COUNTRY_MAP[country]

  return PLAN_META.map((meta, idx) => {
    const price = config.prices[idx]
    const originalPrice = Math.round(price * config.originalMultiplier)
    const perDay = price / PLAN_DAYS[idx]

    return {
      ...meta,
      price,
      originalPrice,
      perDay,
      formattedPrice: formatPrice(price, config.currency),
      formattedOriginalPrice: formatPrice(originalPrice, config.currency),
      formattedPerDay: formatPerDay(perDay, config.currency),
      currency: config.currency,
    }
  })
}

export function getDetectedCurrency(): CurrencyCode {
  const country = detectCountry()
  return COUNTRY_MAP[country].currency
}
